import { resolveImagerySource } from '../../../map/services/imagerySources'
import {
  BADGE_GAP_PT,
  BADGE_TEXT_PT,
  COVERAGE_SCALE,
  FRAME_MARGIN_PT,
  FURNITURE_GAP_PT,
  FURNITURE_MARGIN_PT,
  MAX_SHEETS,
  PX_PER_PT,
  SINGLE_MAP_HEIGHT,
  SINGLE_MAP_WIDTH,
  STANDARD_SCALES,
  TILE_SIZE,
  badgeRect,
  badgeSizePt,
  attributionBoxPt,
  fitZoom,
  locatorBoxPt,
  scaleBarBoxPt,
  sheetFramePt,
  singleMapPtPerPx,
  truncateLabel,
  worldPx,
  zoomForScale,
} from './siteMapGeometry'
import type {
  BadgePlacement,
  Corner,
  PointPt,
  SheetFurniture,
  Density,
  LatLng,
  OverviewFrame,
  RectPt,
  SheetPlan,
  SiteMapLayout,
  SiteMapPlan,
  SiteTree,
  StandardScale,
} from './types'

export { parseSiteMapLayout } from './siteMapGeometry'

/**
 * Map book planner (009, research R3–R5; contracts/map-book-planner.md).
 *
 * Decides between the single site map and a map book, partitions the trees
 * into sheets and places every badge. Pure and deterministic: no DOM, no
 * canvas, no network, no Math.random — input is sorted by node number before
 * anything else, and every tie is broken by node number, so the same trees in
 * any order give a deep-equal plan (FR-013).
 *
 * All partitioning happens in "pt500" space: the printed position, in points,
 * of each tree on a 1:500 sheet. Printing at 1:S scales that by 500/S.
 */

export interface PlanInput {
  trees: SiteTree[]
  boundary: LatLng[] | null
  layout: SiteMapLayout
  density: Density
  /** Badge text size in pt; only the calibration script (Q7) overrides it */
  textPt?: number
}

/** Every scale a sheet may take, finest first: the standard ladder plus the coverage-only 1:10000 */
const SCALE_LADDER: StandardScale[] = [...STANDARD_SCALES, COVERAGE_SCALE]
const REFERENCE_SCALE = 500
/** Ring search for displaced badges (R5): 1.0–12.0 × badge height, 12 bearings */
const RING_MIN = 1
const RING_MAX = 12
const RING_STEP = 0.5
const RING_BEARINGS = 12
/** Floating-point slack on frame fits, so a plan never fails its own re-check */
const FIT_EPSILON = 1e-6

const isLocated = (t: SiteTree) => Number.isFinite(t.latitude) && Number.isFinite(t.longitude)

interface PlannedTree {
  tree: SiteTree
  label: string
  textPt: number
  /** Printed position on a 1:500 sheet, pt */
  x: number
  y: number
  /** Badge footprint, pt */
  w: number
  h: number
}

interface Region {
  trees: PlannedTree[]
  /** Memoised bestScale (undefined = not computed yet) */
  best?: StandardScale | null
}

export function planSiteMap({
  trees,
  boundary,
  layout,
  density,
  textPt = BADGE_TEXT_PT,
}: PlanInput): SiteMapPlan {
  const located = trees.filter(isLocated).sort((a, b) => a.nodeNumber - b.nodeNumber)
  const boundaryPts = (boundary ?? []).filter((p) => isLocated({ ...p, nodeNumber: 0, label: '' }))
  if (located.length === 0 && boundaryPts.length < 3) return { mode: 'none' }

  const overview = overviewFrame([...located, ...boundaryPts])
  if (located.length === 0) return { mode: 'single', frame: overview, placements: [] }

  const centerLatitude =
    (Math.min(...located.map((t) => t.latitude)) + Math.max(...located.map((t) => t.latitude))) / 2
  const refZoom = zoomForScale(REFERENCE_SCALE, centerLatitude)
  const planned = planTrees(located, refZoom, textPt)

  // Automatic keeps the single map unless a map book would actually read
  // better: overlaps no scale can separate get leader lines here too
  if (layout !== 'book') {
    const singleMap = layoutSingleMap(planned, overview, density, layout === 'auto')
    if (layout === 'single' || singleMap.readable) {
      return { mode: 'single', frame: overview, placements: singleMap.placements }
    }
  }

  // The imagery credit's worst case: both sources credited, or the offline note
  // (same centre the renderer resolves its imagery from)
  const lngs = located.map((t) => t.longitude)
  const imagery = resolveImagerySource(centerLatitude, (Math.min(...lngs) + Math.max(...lngs)) / 2)
  const credits = [imagery.primary, imagery.underlay]
    .filter((src) => src !== null)
    .map((src) => src.attributionPlain)
    .join(' · ')
  const frame: Frame = {
    ...sheetFramePt(density),
    creditChars: Math.max(credits.length, 'Imagery unavailable'.length),
  }

  const regions = partition(planned, frame)
  const sheets = numberSheets(
    regions.map((r) => buildSheet(r, frame, centerLatitude, refZoom, planned)),
    frame
  )

  const sheetByNode = new Map<number, number>()
  for (const s of sheets) for (const t of s.trees) sheetByNode.set(t.nodeNumber, s.number)

  overview.sheetOutlines = sheets.map((s) => {
    const c = worldPx(s.center, overview.zoom)
    const k = PX_PER_PT * 2 ** (overview.zoom - s.zoom)
    const w = s.frame.widthPt * k
    const h = s.frame.heightPt * k
    return {
      number: s.number,
      rect: { x: c.x - overview.originX - w / 2, y: c.y - overview.originY - h / 2, w, h },
      label: `${s.number} · ${s.trees.length} trees`,
    }
  })

  return { mode: 'book', overview, sheets, sheetByNode }
}

// ---------------------------------------------------------------------------
// Mode choice (R3)
// ---------------------------------------------------------------------------

/** The single map's framing: fit zoom over trees + boundary in 1400 × 900 */
function overviewFrame(points: LatLng[]): OverviewFrame {
  const lats = points.map((p) => p.latitude)
  const lngs = points.map((p) => p.longitude)
  const center = {
    latitude: (Math.min(...lats) + Math.max(...lats)) / 2,
    longitude: (Math.min(...lngs) + Math.max(...lngs)) / 2,
  }
  const { primary } = resolveImagerySource(center.latitude, center.longitude)
  const zoom = fitZoom(points, primary.maxNativeZoom)
  const xs = points.map((p) => worldPx(p, zoom).x)
  const ys = points.map((p) => worldPx(p, zoom).y)
  return {
    zoom,
    originX: (Math.min(...xs) + Math.max(...xs)) / 2 - SINGLE_MAP_WIDTH / 2,
    originY: (Math.min(...ys) + Math.max(...ys)) / 2 - SINGLE_MAP_HEIGHT / 2,
    width: SINGLE_MAP_WIDTH,
    height: SINGLE_MAP_HEIGHT,
    sheetOutlines: [],
  }
}

/**
 * Small jobs (owner decision after the prod audit): Automatic keeps the single
 * map despite a few overlaps a finer scale would separate — at most 3, or 10%
 * of the trees if that is more — as long as every leader stays short. Past
 * that, a map book reads better.
 */
const SINGLE_MAP_TOLERATED_OVERLAPS = 3
const SINGLE_MAP_TOLERATED_SHARE = 0.1
/** Longest leader (× badge height) a tolerated overlap may need */
const SHORT_LEADER_BADGES = 4

const avoidableLimit = (trees: number) =>
  Math.max(SINGLE_MAP_TOLERATED_OVERLAPS, Math.floor(trees * SINGLE_MAP_TOLERATED_SHARE))

function leadersShort(placements: BadgePlacement[]): boolean {
  return placements.every((pl) => {
    if (!pl.displaced) return true
    const cx = pl.badge.x + pl.badge.w / 2
    const cy = pl.badge.y + pl.badge.h / 2
    return Math.hypot(cx - pl.anchor.x, cy - pl.anchor.y) <= SHORT_LEADER_BADGES * pl.badge.h
  })
}

/** Height of the single map's imagery credit strip, canvas px (drawAttribution) */
const SINGLE_MAP_CREDIT_PX = 22

/**
 * Badges on the single map, in printed pt. Readable when every overlap is
 * one no scale could separate (it still collides at 1:500, so a map book
 * would only move it onto a sheet with the same leader line) and placement
 * finds room for those badges — the same rule the sheets use (R4 amendment).
 */
/** Located trees (sorted by node number) in pt500 space with their badges */
function planTrees(located: SiteTree[], refZoom: number, textPt: number): PlannedTree[] {
  return located.map((tree) => {
    const label = truncateLabel(tree.label)
    const p = worldPx(tree, refZoom)
    return {
      tree,
      label,
      textPt,
      x: p.x / PX_PER_PT,
      y: p.y / PX_PER_PT,
      ...badgeSizePt(label, textPt),
    }
  })
}

/** Each tree's position on the single map, printed pt */
function singleMapAnchors(planned: PlannedTree[], frame: OverviewFrame, density: Density) {
  const ptPerPx = singleMapPtPerPx(density)
  return planned.map((t) => {
    const p = worldPx(t.tree, frame.zoom)
    return { t, x: (p.x - frame.originX) * ptPerPx, y: (p.y - frame.originY) * ptPerPx }
  })
}

function layoutSingleMap(
  planned: PlannedTree[],
  frame: OverviewFrame,
  density: Density,
  /** Automatic: only readability matters until it passes, so stop at the first failure */
  checkOnly: boolean
): { readable: boolean; placements: BadgePlacement[] } {
  const ptPerPx = singleMapPtPerPx(density)
  const anchors = singleMapAnchors(planned, frame, density)
  // Badges may not be displaced under the credit strip along the bottom
  const area = {
    widthPt: frame.width * ptPerPx,
    heightPt: (frame.height - SINGLE_MAP_CREDIT_PX) * ptPerPx,
  }
  const limit = avoidableLimit(planned.length)
  const avoidable = countAvoidableOverlaps(
    planned,
    anchors.map((a) => badgeRect(a, a.t.label, a.t.textPt)),
    limit
  )
  if (avoidable > limit && checkOnly) return { readable: false, placements: [] }
  const { placements, crowded } = placeBadges(anchors, area, checkOnly)
  const readable = !crowded && (avoidable === 0 || (avoidable <= limit && leadersShort(placements)))
  return { readable, placements }
}

// ---------------------------------------------------------------------------
// Partitioning (R4)
// ---------------------------------------------------------------------------

type Frame = {
  widthPt: number
  heightPt: number
  /** Longest imagery credit the sheet may print (sizes its strip) */
  creditChars?: number
}

function bbox(trees: PlannedTree[]) {
  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity
  let maxW = 0
  let h = 0
  for (const t of trees) {
    minX = Math.min(minX, t.x)
    maxX = Math.max(maxX, t.x)
    minY = Math.min(minY, t.y)
    maxY = Math.max(maxY, t.y)
    maxW = Math.max(maxW, t.w)
    h = Math.max(h, t.h)
  }
  return { minX, minY, maxX, maxY, maxW, h }
}

/** Region's trees, each inflated by half its badge plus the frame margin, fit the frame at 1:scale */
function fits(region: Region, scale: number, frame: Frame): boolean {
  const b = bbox(region.trees)
  const k = REFERENCE_SCALE / scale
  const spanX = (b.maxX - b.minX) * k + b.maxW + 2 * FRAME_MARGIN_PT
  const spanY = (b.maxY - b.minY) * k + b.h + 2 * FRAME_MARGIN_PT
  return spanX <= frame.widthPt - FIT_EPSILON && spanY <= frame.heightPt - FIT_EPSILON
}

/** Region anchors in frame pt at 1:scale, framed on the region's bbox centre
    moved by `shift` (pt) — the slack a frame has around its content */
function frameAnchors(region: Region, scale: number, frame: Frame, shift = NO_SHIFT) {
  const k = REFERENCE_SCALE / scale
  const b = bbox(region.trees)
  const cx = (b.minX + b.maxX) / 2
  const cy = (b.minY + b.maxY) / 2
  return region.trees.map((t) => ({
    t,
    x: (t.x - cx) * k + frame.widthPt / 2 + shift.dx,
    y: (t.y - cy) * k + frame.heightPt / 2 + shift.dy,
  }))
}

/**
 * True when some pair of badges collides at 1:scale but not at 1:500 — an
 * overlap a finer scale would avoid. Spatial hash (cell = widest badge);
 * stops at the first such pair.
 */
function hasAvoidableOverlap(region: Region, scale: number): boolean {
  const k = REFERENCE_SCALE / scale
  return hasAvoidableOverlapAt(
    region.trees,
    region.trees.map((t) => badgeRect({ x: t.x * k, y: t.y * k }, t.label, t.textPt))
  )
}

/** `at` holds each tree's badge as printed (same order as `trees`); true when
    two collide there but would not at 1:500 */
function hasAvoidableOverlapAt(trees: PlannedTree[], at: RectPt[]): boolean {
  return countAvoidableOverlaps(trees, at, 0) > 0
}

/** Badge pairs colliding in `at` but not at 1:500, counted up to `stopAfter + 1` */
function countAvoidableOverlaps(trees: PlannedTree[], at: RectPt[], stopAfter: number): number {
  const badges = trees.map((t, i) => ({
    at: at[i] ?? badgeRect(t, t.label, t.textPt),
    finest: badgeRect(t, t.label, t.textPt),
  }))
  if (badges.length < 2) return 0
  const cell = Math.max(...badges.map((b) => Math.max(b.at.w, b.at.h))) + BADGE_GAP_PT
  const grid = new Map<string, typeof badges>()
  let count = 0
  for (const b of badges) {
    const cx = Math.floor((b.at.x + b.at.w / 2) / cell)
    const cy = Math.floor((b.at.y + b.at.h / 2) / cell)
    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        for (const o of grid.get(`${cx + dx},${cy + dy}`) ?? []) {
          if (collides(b.at, o.at) && !collides(b.finest, o.finest) && ++count > stopAfter) {
            return count
          }
        }
      }
    }
    const key = `${cx},${cy}`
    const bucket = grid.get(key)
    if (bucket) bucket.push(b)
    else grid.set(key, [b])
  }
  return count
}

/**
 * Readable at 1:scale: no overlap that a finer scale could avoid. Pairs that
 * still collide at 1:500 (multi-stem trees, hedgerows) are irreducible — no
 * scale separates them — so they go to leader lines instead of driving
 * endless splits, as long as placement can actually find room for them.
 */
function readable(region: Region, scale: number, frame: Frame): boolean {
  if (scale !== REFERENCE_SCALE && hasAvoidableOverlap(region, scale)) return false
  // Every badge placed, and a clear corner for each piece of sheet furniture
  return layoutSheet(region, scale, frame, [], true).clear
}

/** Coarsest standard scale (≤ 1:5000) at which the region fits and is readable */
function bestScale(region: Region, frame: Frame): StandardScale | null {
  if (region.best !== undefined) return region.best
  region.best = null
  for (const scale of [...STANDARD_SCALES].reverse()) {
    if (fits(region, scale, frame) && readable(region, scale, frame)) {
      region.best = scale
      break
    }
  }
  return region.best
}

/** A gap this share of the region's span is split at instead of the median */
const NATURAL_BREAK = 0.25

/** Regions are never empty; 0 only satisfies the type checker */
const firstNode = (r: Region) => r.trees[0]?.tree.nodeNumber ?? 0
const byNode = (a: PlannedTree, b: PlannedTree) => a.tree.nodeNumber - b.tree.nodeNumber

/** Median split along the region's longer axis relative to the landscape frame */
function split(region: Region, frame: Frame): [Region, Region] {
  const b = bbox(region.trees)
  const alongX = (b.maxX - b.minX) / frame.widthPt >= (b.maxY - b.minY) / frame.heightPt
  const sorted = [...region.trees].sort((p, q) =>
    alongX ? p.x - q.x || byNode(p, q) : p.y - q.y || byNode(p, q)
  )
  const at = (t: PlannedTree) => (alongX ? t.x : t.y)
  // A natural break (an outlier, two separate groves) beats the median: the
  // median would peel the far group off a few trees at a time
  const positions = sorted.map(at)
  const span = Math.max(...positions) - Math.min(...positions)
  let cut = Math.floor(sorted.length / 2)
  let widest = 0
  positions.forEach((p, i) => {
    const gap = i > 0 ? p - (positions[i - 1] ?? p) : 0
    if (gap > widest) {
      widest = gap
      if (gap >= NATURAL_BREAK * span) cut = i
    }
  })
  return [{ trees: sorted.slice(0, cut).sort(byNode) }, { trees: sorted.slice(cut).sort(byNode) }]
}

/**
 * Best-first k-d splitting. Regions a 1:5000 frame cannot cover are split
 * first (coverage guard), then the unreadable region with the most trees,
 * until every region has a readable scale or the 12-sheet cap is hit.
 */
function partition(trees: PlannedTree[], frame: Frame): Region[] {
  let regions: Region[] = [{ trees }]
  while (regions.length < MAX_SHEETS) {
    const pending = regions.filter((r) => bestScale(r, frame) === null && r.trees.length > 1)
    if (pending.length === 0) break
    const rank = (r: Region) => (fits(r, 5000, frame) ? 0 : 1)
    pending.sort(
      (a, b) => rank(b) - rank(a) || b.trees.length - a.trees.length || firstNode(a) - firstNode(b)
    )
    const [target] = pending
    regions = regions.flatMap((r) => (r === target ? split(r, frame) : [r]))
  }
  return regions
}

// ---------------------------------------------------------------------------
// Sheets and badge placement (R5)
// ---------------------------------------------------------------------------

/** Inverse Web Mercator: world px at `zoom` → lat/lng */
function latLngAt(x: number, y: number, zoom: number): LatLng {
  const size = TILE_SIZE * 2 ** zoom
  const n = Math.PI * (1 - (2 * y) / size)
  return {
    latitude: (Math.atan(Math.sinh(n)) * 180) / Math.PI,
    longitude: (x / size) * 360 - 180,
  }
}

function buildSheet(
  region: Region,
  frame: Frame,
  centerLatitude: number,
  refZoom: number,
  allTrees: PlannedTree[]
): Omit<SheetPlan, 'number'> {
  const legibleScale = bestScale(region, frame)
  // Past the cap: the finest scale whose frame still covers the region, with
  // leader lines carrying the overlaps (R4 step 5); 1:10000 is the last resort
  const scale = legibleScale ?? SCALE_LADDER.find((s) => fits(region, s, frame)) ?? COVERAGE_SCALE
  const k = REFERENCE_SCALE / scale
  const b = bbox(region.trees)
  const zoom = zoomForScale(scale, centerLatitude)

  // Furniture clears other sheets' trees too where it can; this region's own
  // trees, badges and leaders always come first
  const own = new Set(region.trees.map((t) => t.tree.nodeNumber))
  const others = allTrees.filter((t) => !own.has(t.tree.nodeNumber))
  let layout = layoutSheet(region, scale, frame, others, false)
  if (!layout.clear && others.length > 0) {
    const ownOnly = layoutSheet(region, scale, frame, [], false)
    if (ownOnly.clear) layout = ownOnly
  }
  const cx = (b.minX + b.maxX) / 2 - layout.shift.dx / k
  const cy = (b.minY + b.maxY) / 2 - layout.shift.dy / k

  return {
    scale,
    zoom,
    tileZoom: Math.floor(zoom),
    center: latLngAt(cx * PX_PER_PT, cy * PX_PER_PT, refZoom),
    frame: { widthPt: frame.widthPt, heightPt: frame.heightPt },
    trees: region.trees.map((t) => t.tree),
    placements: layout.placements,
    crowded: !layout.clear,
    furniture: layout.furniture,
  }
}

// ---------------------------------------------------------------------------
// Sheet furniture: locator inset, scale bar, imagery credit
// ---------------------------------------------------------------------------

const NO_SHIFT = { dx: 0, dy: 0 }

interface Segment {
  from: PointPt
  to: PointPt
}

interface FurnitureSizes {
  locator: { w: number; h: number }
  scaleBar: { w: number; h: number }
  attribution: { w: number; h: number }
}

const CORNER_PREFERENCE: Record<keyof FurnitureSizes, Corner[]> = {
  locator: ['tr', 'tl', 'br', 'bl'],
  scaleBar: ['bl', 'br', 'tl', 'tr'],
  attribution: ['br', 'bl', 'tr', 'tl'],
}

function cornerRect(corner: Corner, size: { w: number; h: number }, frame: Frame, margin: number) {
  return {
    x: corner === 'tl' || corner === 'bl' ? margin : frame.widthPt - margin - size.w,
    y: corner === 'tl' || corner === 'tr' ? margin : frame.heightPt - margin - size.h,
    w: size.w,
    h: size.h,
  }
}

const rectsOverlap = (a: RectPt, b: RectPt) =>
  a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h

/** Liang–Barsky: does the segment pass through the rect's interior? */
function segmentCrosses(seg: Segment, r: RectPt): boolean {
  const dx = seg.to.x - seg.from.x
  const dy = seg.to.y - seg.from.y
  let t0 = 0
  let t1 = 1
  const clip = (p: number, q: number) => {
    if (p === 0) return q > 0
    const t = q / p
    if (p < 0) {
      if (t > t1) return false
      if (t > t0) t0 = t
    } else {
      if (t < t0) return false
      if (t < t1) t1 = t
    }
    return true
  }
  return (
    clip(-dx, seg.from.x - r.x) &&
    clip(dx, r.x + r.w - seg.from.x) &&
    clip(-dy, seg.from.y - r.y) &&
    clip(dy, r.y + r.h - seg.from.y) &&
    t0 < t1
  )
}

/** How many obstacles a rect (inflated by the furniture gap) would cover */
function hits(r: RectPt, rects: RectPt[], segments: Segment[]): number {
  const g = FURNITURE_GAP_PT
  const big = { x: r.x - g, y: r.y - g, w: r.w + 2 * g, h: r.h + 2 * g }
  let n = 0
  for (const o of rects) if (rectsOverlap(big, o)) n++
  for (const sg of segments) if (segmentCrosses(sg, big)) n++
  return n
}

function furnitureCombos(sizes: FurnitureSizes, frame: Frame) {
  const combos: Array<{ rank: number; furniture: SheetFurniture }> = []
  CORNER_PREFERENCE.locator.forEach((lc, li) =>
    CORNER_PREFERENCE.scaleBar.forEach((sc, si) =>
      CORNER_PREFERENCE.attribution.forEach((ac, ai) => {
        if (lc === sc || lc === ac || sc === ac) return
        combos.push({
          // Preference order, ties broken locator first, then scale bar
          rank: (li + si + ai) * 100 + li * 10 + si,
          furniture: {
            locator: cornerRect(lc, sizes.locator, frame, FURNITURE_MARGIN_PT),
            scaleBar: cornerRect(sc, sizes.scaleBar, frame, FURNITURE_MARGIN_PT),
            attribution: {
              ...cornerRect(ac, sizes.attribution, frame, 0),
              align: ac === 'tl' || ac === 'bl' ? 'left' : 'right',
            },
          },
        })
      })
    )
  )
  return combos.sort((a, b) => a.rank - b.rank).map((c) => c.furniture)
}

const furnitureItems = (f: SheetFurniture) => [f.locator, f.scaleBar, f.attribution]

/**
 * Corners for the locator inset, scale bar and imagery credit that cover none
 * of the obstacles (badges, tree dots, leader lines), most preferred first.
 * Null when every arrangement covers something.
 */
export function assignFurniture(
  rects: RectPt[],
  segments: Segment[],
  frame: { widthPt: number; heightPt: number },
  sizes: FurnitureSizes
): SheetFurniture | null {
  for (const f of furnitureCombos(sizes, frame)) {
    const [a, b, c] = furnitureItems(f) as [RectPt, RectPt, RectPt]
    if (rectsOverlap(a, b) || rectsOverlap(a, c) || rectsOverlap(b, c)) continue
    if (furnitureItems(f).every((r) => hits(r, rects, segments) === 0)) return f
  }
  return null
}

/** Last resort (cap reached on a packed sheet): the arrangement covering least */
function leastCoveringFurniture(
  rects: RectPt[],
  segments: Segment[],
  frame: Frame,
  sizes: FurnitureSizes
): SheetFurniture {
  let best: { f: SheetFurniture; n: number } | null = null
  for (const f of furnitureCombos(sizes, frame)) {
    const n = furnitureItems(f).reduce((sum, r) => sum + hits(r, rects, segments), 0)
    if (!best || n < best.n) best = { f, n }
  }
  // furnitureCombos is never empty (24 arrangements of 4 corners)
  return (best as { f: SheetFurniture }).f
}

function furnitureSizes(scale: number, frame: Frame): FurnitureSizes {
  return {
    locator: locatorBoxPt(frame),
    scaleBar: scaleBarBoxPt(frame, scale),
    attribution: attributionBoxPt(frame.creditChars ?? 0),
  }
}

/** What the furniture must not cover: badges, tree dots, leader lines */
function obstacles(placements: BadgePlacement[], otherDots: PointPt[]) {
  const rects: RectPt[] = []
  const segments: Segment[] = []
  const dot = (p: PointPt) => ({ x: p.x - 2, y: p.y - 2, w: 4, h: 4 })
  for (const pl of placements) {
    rects.push(pl.badge, dot(pl.anchor))
    if (pl.displaced) {
      segments.push({
        from: pl.anchor,
        to: { x: pl.badge.x + pl.badge.w / 2, y: pl.badge.y + pl.badge.h / 2 },
      })
    }
  }
  for (const p of otherDots) rects.push(dot(p))
  return { rects, segments }
}

interface SheetLayout {
  shift: { dx: number; dy: number }
  placements: BadgePlacement[]
  furniture: SheetFurniture
  /** Badges placed without overlap and furniture covering nothing */
  clear: boolean
}

/**
 * Badges and furniture for a region at a scale. The frame is centred on the
 * trees first; if no arrangement of corners is clear, the content slides
 * within the frame's slack (half of it, each way) to open a corner.
 */
function layoutSheet(
  region: Region,
  scale: number,
  frame: Frame,
  others: PlannedTree[],
  /** Feasibility check only: stop at the first failure */
  abort: boolean
): SheetLayout {
  const k = REFERENCE_SCALE / scale
  const b = bbox(region.trees)
  const slackX = Math.max(0, frame.widthPt - ((b.maxX - b.minX) * k + b.maxW + 2 * FRAME_MARGIN_PT))
  const slackY = Math.max(0, frame.heightPt - ((b.maxY - b.minY) * k + b.h + 2 * FRAME_MARGIN_PT))
  const sx = slackX / 2
  const sy = slackY / 2
  const shifts = [
    NO_SHIFT,
    { dx: -sx, dy: 0 },
    { dx: sx, dy: 0 },
    { dx: 0, dy: sy },
    { dx: 0, dy: -sy },
    { dx: -sx, dy: sy },
    { dx: sx, dy: sy },
    { dx: -sx, dy: -sy },
    { dx: sx, dy: -sy },
  ].filter((sh, i) => i === 0 || sh.dx !== 0 || sh.dy !== 0)
  const sizes = furnitureSizes(scale, frame)
  const cx = (b.minX + b.maxX) / 2
  const cy = (b.minY + b.maxY) / 2

  let first: SheetLayout | null = null
  for (const shift of shifts) {
    const { placements, crowded } = placeBadges(
      frameAnchors(region, scale, frame, shift),
      frame,
      abort
    )
    if (crowded && abort)
      return {
        shift,
        placements,
        furniture: first?.furniture ?? leastCoveringFurniture([], [], frame, sizes),
        clear: false,
      }
    const otherDots = others
      .map((t) => ({
        x: (t.x - cx) * k + frame.widthPt / 2 + shift.dx,
        y: (t.y - cy) * k + frame.heightPt / 2 + shift.dy,
      }))
      .filter((p) => p.x >= 0 && p.y >= 0 && p.x <= frame.widthPt && p.y <= frame.heightPt)
    const { rects, segments } = obstacles(placements, otherDots)
    const furniture = assignFurniture(rects, segments, frame, sizes)
    if (furniture && !crowded) return { shift, placements, furniture, clear: true }
    first ??= {
      shift,
      placements,
      furniture: furniture ?? leastCoveringFurniture(rects, segments, frame, sizes),
      clear: false,
    }
    // Sliding the frame cannot separate badges from each other
    if (crowded) break
  }
  return (
    first ?? {
      shift: NO_SHIFT,
      placements: [],
      furniture: leastCoveringFurniture([], [], frame, sizes),
      clear: false,
    }
  )
}

/** Uniform-grid index of placed badge rects */
class RectIndex {
  private cells = new Map<string, RectPt[]>()
  constructor(private size: number) {}

  private keys(r: RectPt): string[] {
    const g = BADGE_GAP_PT
    const out: string[] = []
    const x0 = Math.floor((r.x - g) / this.size)
    const x1 = Math.floor((r.x + r.w + g) / this.size)
    const y0 = Math.floor((r.y - g) / this.size)
    const y1 = Math.floor((r.y + r.h + g) / this.size)
    for (let x = x0; x <= x1; x++) for (let y = y0; y <= y1; y++) out.push(`${x},${y}`)
    return out
  }

  add(r: RectPt) {
    for (const k of this.keys(r)) {
      const cell = this.cells.get(k)
      if (cell) cell.push(r)
      else this.cells.set(k, [r])
    }
  }

  /** Placed rects within the gap of `r` */
  near(r: RectPt): Set<RectPt> {
    const out = new Set<RectPt>()
    for (const k of this.keys(r)) for (const o of this.cells.get(k) ?? []) out.add(o)
    return out
  }
}

const g = BADGE_GAP_PT
const collides = (a: RectPt, b: RectPt) =>
  a.x < b.x + b.w + g && b.x < a.x + a.w + g && a.y < b.y + b.h + g && b.y < a.y + a.h + g

const overlapArea = (a: RectPt, b: RectPt) =>
  Math.max(0, Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x)) *
  Math.max(0, Math.min(a.y + a.h, b.y + b.h) - Math.max(a.y, b.y))

const inFrame = (r: RectPt, frame: Frame) =>
  r.x >= 0 && r.y >= 0 && r.x + r.w <= frame.widthPt && r.y + r.h <= frame.heightPt

/**
 * Greedy, deterministic placement in node order: at the tree when clear,
 * else the first clear in-frame position on rings around it (north first,
 * clockwise). If none exists the least-overlap candidate is used and the
 * sheet is marked crowded — the spec's sole FR-008 exception.
 */
function placeBadges(
  anchors: Array<{ t: PlannedTree; x: number; y: number }>,
  frame: Frame,
  /** Feasibility check only: give up at the first badge that has no clear spot */
  abortWhenCrowded = false
): { placements: BadgePlacement[]; crowded: boolean } {
  const index = new RectIndex(24)
  const placements: BadgePlacement[] = []
  let crowded = false

  for (const a of anchors) {
    const centred = badgeRect(a, a.t.label, a.t.textPt)
    const candidates = function* () {
      yield { rect: centred, displaced: false }
      for (let ring = RING_MIN; ring <= RING_MAX; ring += RING_STEP) {
        const r = ring * a.t.h
        for (let i = 0; i < RING_BEARINGS; i++) {
          const theta = (i * 2 * Math.PI) / RING_BEARINGS
          const rect = badgeRect(
            { x: a.x + r * Math.sin(theta), y: a.y - r * Math.cos(theta) },
            a.t.label,
            a.t.textPt
          )
          if (inFrame(rect, frame)) yield { rect, displaced: true }
        }
      }
    }

    let chosen: { rect: RectPt; displaced: boolean } | null = null
    let fallback: { rect: RectPt; displaced: boolean; overlap: number } | null = null
    for (const c of candidates()) {
      const near = [...index.near(c.rect)].filter((o) => collides(c.rect, o))
      if (near.length === 0) {
        chosen = c
        break
      }
      const overlap = near.reduce((sum, o) => sum + overlapArea(c.rect, o), 0)
      if (!fallback || overlap < fallback.overlap) fallback = { ...c, overlap }
    }
    if (!chosen) {
      crowded = true
      if (abortWhenCrowded) break
      // The centred candidate is always tried first, so a fallback exists
      chosen = fallback ?? { rect: centred, displaced: false }
    }
    index.add(chosen.rect)
    placements.push({
      nodeNumber: a.t.tree.nodeNumber,
      anchor: { x: a.x, y: a.y },
      badge: chosen.rect,
      displaced: chosen.displaced,
    })
  }
  return { placements, crowded }
}

// ---------------------------------------------------------------------------
// Numbering (R4 step 8)
// ---------------------------------------------------------------------------

/** Rows north→south (a sheet joins a row when its centre is within half a
    frame height of the row's first sheet), west→east within a row. */
function numberSheets(sheets: Array<Omit<SheetPlan, 'number'>>, frame: Frame): SheetPlan[] {
  const z = 22
  const pos = sheets.map((s) => {
    const w = worldPx(s.center, z)
    return {
      s,
      x: w.x,
      y: w.y,
      halfH: (frame.heightPt / 2) * PX_PER_PT * 2 ** (z - s.zoom),
      first: s.trees[0]?.nodeNumber ?? 0,
    }
  })
  pos.sort((a, b) => a.y - b.y || a.first - b.first)
  const rows: (typeof pos)[] = []
  for (const p of pos) {
    const row = rows[rows.length - 1]
    const head = row?.[0]
    if (row && head && Math.abs(p.y - head.y) <= head.halfH) row.push(p)
    else rows.push([p])
  }
  return rows
    .flatMap((row) => [...row].sort((a, b) => a.x - b.x || a.first - b.first))
    .map((p, i) => ({ number: i + 1, ...p.s }))
}

// ---------------------------------------------------------------------------
// Diagnostics (misc/scripts/map-book-audit.ts --explain)
// ---------------------------------------------------------------------------

/** Ground metres per pt500 unit: 1 printed pt at 1:500 (Mercator-corrected via refZoom) */
const METRES_PER_PT500 = 0.000352778 * REFERENCE_SCALE

export interface SiteMapExplanation {
  mode: SiteMapPlan['mode']
  /** Closest pair of located trees; null with fewer than two */
  closest: { a: number; b: number; distanceM: number } | null
  /** Badge pairs that collide on the single map. Irreducible ones still
      collide at 1:500, so only leader lines help; any avoidable one makes
      Automatic choose a map book. */
  overlaps: Array<{ a: number; b: number; distanceM: number; irreducible: boolean }>
  /** Badge placement on the single map found a clear spot for every badge */
  placementRoom: boolean
  /** Avoidable overlaps Automatic tolerates on this job's single map */
  avoidableLimit: number
  /** Every leader on the single map is short enough to tolerate */
  leadersShort: boolean
}

/** Why Automatic chose what it did — read-only, for the calibration audit */
export function explainSiteMapChoice(input: PlanInput): SiteMapExplanation {
  const mode = planSiteMap(input).mode
  const textPt = input.textPt ?? BADGE_TEXT_PT
  const located = input.trees.filter(isLocated).sort((a, b) => a.nodeNumber - b.nodeNumber)
  if (located.length === 0) {
    return {
      mode,
      closest: null,
      overlaps: [],
      placementRoom: true,
      avoidableLimit: avoidableLimit(0),
      leadersShort: true,
    }
  }

  const boundaryPts = (input.boundary ?? []).filter((p) =>
    isLocated({ ...p, nodeNumber: 0, label: '' })
  )
  const overview = overviewFrame([...located, ...boundaryPts])
  const centerLatitude =
    (Math.min(...located.map((t) => t.latitude)) + Math.max(...located.map((t) => t.latitude))) / 2
  const planned = planTrees(located, zoomForScale(REFERENCE_SCALE, centerLatitude), textPt)
  const metres = (p: PlannedTree, q: PlannedTree) =>
    Math.hypot(p.x - q.x, p.y - q.y) * METRES_PER_PT500

  let closest: SiteMapExplanation['closest'] = null
  for (const [i, p] of planned.entries()) {
    for (const q of planned.slice(i + 1)) {
      const d = metres(p, q)
      if (!closest || d < closest.distanceM) {
        closest = { a: p.tree.nodeNumber, b: q.tree.nodeNumber, distanceM: d }
      }
    }
  }

  const anchors = singleMapAnchors(planned, overview, input.density)
  const rects = anchors.map((a) => badgeRect(a, a.t.label, a.t.textPt))
  const overlaps: SiteMapExplanation['overlaps'] = []
  for (const [i, p] of planned.entries()) {
    for (const [j, q] of planned.entries()) {
      if (j <= i) continue
      const ri = rects[i]
      const rj = rects[j]
      if (!ri || !rj || !collides(ri, rj)) continue
      overlaps.push({
        a: p.tree.nodeNumber,
        b: q.tree.nodeNumber,
        distanceM: metres(p, q),
        irreducible: collides(badgeRect(p, p.label, p.textPt), badgeRect(q, q.label, q.textPt)),
      })
    }
  }

  const ptPerPx = singleMapPtPerPx(input.density)
  const area = {
    widthPt: overview.width * ptPerPx,
    heightPt: (overview.height - SINGLE_MAP_CREDIT_PX) * ptPerPx,
  }
  const placement = placeBadges(anchors, area)
  return {
    mode,
    closest,
    overlaps,
    placementRoom: !placement.crowded,
    avoidableLimit: avoidableLimit(planned.length),
    leadersShort: leadersShort(placement.placements),
  }
}
